import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const GEO_STORAGE_KEY = 'gopiko_country';
const GEO_MANUAL_KEY = 'gopiko_country_manual';
const DEFAULT_COUNTRY = ''; // empty = show all countries

const GeoContext = createContext(null);

export function GeoProvider({ children }) {
  const [userCountry, setUserCountry] = useState(() => {
    return localStorage.getItem(GEO_STORAGE_KEY) || '';
  });
  const [detectedCountry, setDetectedCountry] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [userCoords, setUserCoords] = useState({ lat: 0, lng: 0 });
  const [loading, setLoading] = useState(true);

  // Detect country from IP on first visit (only if user hasn't manually chosen)
  useEffect(() => {
    const detect = async () => {
      try {
        const data = await api.detectLocation();
        if (data.country && data.country !== 'Unknown') {
          setDetectedCountry(data.country);
          // Auto-set userCountry from IP if no manual choice has been made
          const hasManualChoice = localStorage.getItem(GEO_MANUAL_KEY);
          if (!hasManualChoice) {
            setUserCountry(data.country);
            localStorage.setItem(GEO_STORAGE_KEY, data.country);
          }
        }
        if (data.lat && data.lng) {
          setUserCoords({ lat: data.lat, lng: data.lng });
        }
      } catch (e) {
        console.warn('Geo detection failed:', e);
      }
      setLoading(false);
    };
    detect();
  }, []);

  // Fetch available countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await api.getAvailableCountries();
        if (data.countries) setAvailableCountries(data.countries);
      } catch (e) {
        console.warn('Failed to fetch countries:', e);
      }
    };
    fetchCountries();
  }, []);

  const setCountry = useCallback((country) => {
    setUserCountry(country);
    localStorage.setItem(GEO_MANUAL_KEY, '1');
    if (country) {
      localStorage.setItem(GEO_STORAGE_KEY, country);
    } else {
      localStorage.removeItem(GEO_STORAGE_KEY);
    }
  }, []);

  const value = {
    userCountry,
    detectedCountry,
    availableCountries,
    userCoords,
    loading,
    setCountry,
  };

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
}

export function useGeo() {
  const ctx = useContext(GeoContext);
  if (!ctx) return {
    userCountry: '', detectedCountry: '', availableCountries: [],
    loading: false, setCountry: () => {},
  };
  return ctx;
}

export default GeoContext;
